import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calculator,
  ArrowRight,
  AlertTriangle,
  Route,
  Weight,
  Truck,
  CheckCircle2,
} from "lucide-react";
import AddressInput from "@/components/AddressInput";
import { haversineKm, estimatePrice } from "@/lib/pricing";
import { CALCULATOR_VEHICLES, PRICING_RULES, COMPANY } from "@/config/site";

const formatEuro = (n) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n);

// Calculateur de devis — version "compact" (accueil) ou "full" (page dédiée)
const QuoteCalculator = ({ variant = "full" }) => {
  const navigate = useNavigate();
  const dark = variant === "compact";

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [fromGeo, setFromGeo] = useState(null);
  const [toGeo, setToGeo] = useState(null);
  const [weight, setWeight] = useState("");
  const [vehicleId, setVehicleId] = useState(CALCULATOR_VEHICLES[1].id);
  const [options, setOptions] = useState({
    demenagement: false,
    hayon: false,
    urgence: false,
  });
  const [result, setResult] = useState(null);
  const [geoError, setGeoError] = useState("");

  const vehicle = useMemo(
    () => CALCULATOR_VEHICLES.find((v) => v.id === vehicleId),
    [vehicleId]
  );

  // Calcul en temps réel dès que les entrées sont complètes
  useEffect(() => {
    setGeoError("");
    if (!fromGeo || !toGeo || !weight || Number(weight) < 1) {
      setResult(null);
      return;
    }
    const distanceKm =
      haversineKm(fromGeo.lat, fromGeo.lon, toGeo.lat, toGeo.lon) *
      PRICING_RULES.roadFactor;
    const est = estimatePrice({
      distanceKm,
      weightKg: Number(weight),
      vehicle,
      options,
    });
    if (!est.ok) {
      setGeoError(est.error);
      setResult(null);
    } else {
      setResult(est);
    }
  }, [fromGeo, toGeo, weight, vehicle, options]);

  const toggle = (key) =>
    setOptions((o) => ({ ...o, [key]: !o[key] }));

  const askQuote = () => {
    navigate("/contact", {
      state: {
        prefill: {
          prestation: options.demenagement
            ? "Déménagement"
            : "Transport de marchandises",
          adresseEnlevement: from,
          adresseLivraison: to,
          poids: weight,
          vehicule: vehicle?.name || "",
          options,
          distanceKm: result?.distanceKm,
          estimation: result ? `${result.low} € – ${result.high} € HT` : "",
        },
      },
    });
  };

  const labelCls = `mb-2 block text-sm font-medium ${dark ? "text-slate-300" : "text-slate-700"}`;
  const inputCls = dark
    ? "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3.5 text-base text-[#FAFAF7] placeholder:text-slate-500 focus:border-secondary focus:outline-none focus:ring-2 focus:ring-secondary/40"
    : "w-full rounded-xl border border-slate-300 bg-white px-4 py-3.5 text-base text-foreground placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30";

  return (
    <div
      data-testid={`quote-calculator-${variant}`}
      className={
        dark
          ? "relative overflow-hidden rounded-3xl border border-white/10 bg-[#111418] p-6 shadow-[0_20px_50px_rgba(37,99,235,0.15)] sm:p-8"
          : "relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_20px_50px_rgba(37,99,235,0.15)] sm:p-8"
      }
    >
      <div className="mb-6 flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent/15">
          <Calculator className="h-6 w-6 text-accent" strokeWidth={2} />
        </span>
        <div>
          <h2 className={`font-heading text-xl font-bold ${dark ? "text-[#FAFAF7]" : "text-foreground"}`}>
            Estimez votre transport
          </h2>
          <p className={`text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>
            Estimation indicative en quelques secondes, sans engagement.
          </p>
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <AddressInput
          id={`${variant}-from`}
          label="Adresse de départ"
          placeholder="Ex. : Troyes, 10 rue de la Paix…"
          value={from}
          onChange={setFrom}
          onSelect={setFromGeo}
          testid={`calc-${variant}-from-input`}
          dark={dark}
        />
        <AddressInput
          id={`${variant}-to`}
          label="Adresse d'arrivée"
          placeholder="Ex. : Lyon, 5 avenue Berthelot…"
          value={to}
          onChange={setTo}
          onSelect={setToGeo}
          testid={`calc-${variant}-to-input`}
          dark={dark}
        />
        <div>
          <label htmlFor={`${variant}-weight`} className={labelCls}>
            Poids de la marchandise (kg)
          </label>
          <div className="relative">
            <Weight
              className={`pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 ${dark ? "text-slate-500" : "text-slate-400"}`}
            />
            <input
              id={`${variant}-weight`}
              type="number"
              min="1"
              max={PRICING_RULES.maxWeightKg}
              data-testid={`calc-${variant}-weight-input`}
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="Ex. : 800"
              className={`${inputCls} pl-11`}
            />
          </div>
        </div>
        <div>
          <label htmlFor={`${variant}-vehicle`} className={labelCls}>
            Type de véhicule
          </label>
          <div className="relative">
            <Truck
              className={`pointer-events-none absolute left-3.5 top-1/2 h-5 w-5 -translate-y-1/2 ${dark ? "text-slate-500" : "text-slate-400"}`}
            />
            <select
              id={`${variant}-vehicle`}
              data-testid={`calc-${variant}-vehicle-select`}
              value={vehicleId}
              onChange={(e) => setVehicleId(e.target.value)}
              className={`${inputCls} appearance-none pl-11 ${dark ? "[&>option]:bg-[#111418]" : ""}`}
            >
              {CALCULATOR_VEHICLES.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} — {v.payload.toLocaleString("fr-FR")} kg, ~{v.volume} m³
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <fieldset className="mt-5">
        <legend className="sr-only">Options</legend>
        <div className="flex flex-wrap gap-3">
          {[
            ["demenagement", "Déménagement complet"],
            ["hayon", "Hayon élévateur"],
            ["urgence", "Urgence < 24 h"],
          ].map(([key, label]) => (
            <label
              key={key}
              data-testid={`calc-${variant}-option-${key}`}
              className={`flex cursor-pointer items-center gap-2 rounded-full border px-4 py-2.5 text-sm font-medium transition-[background-color,border-color,color] duration-200 ${
                options[key]
                  ? "border-accent bg-accent/15 text-accent"
                  : dark
                    ? "border-white/15 text-slate-300 hover:border-white/30"
                    : "border-slate-300 text-slate-600 hover:border-slate-400"
              }`}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={options[key]}
                onChange={() => toggle(key)}
              />
              {options[key] && <CheckCircle2 className="h-4 w-4" />}
              {label}
            </label>
          ))}
        </div>
      </fieldset>

      {/* Erreurs */}
      {geoError && (
        <div
          role="alert"
          data-testid={`calc-${variant}-error`}
          className="mt-5 flex items-start gap-3 rounded-xl border border-amber-300/40 bg-amber-500/10 p-4 text-sm text-amber-500"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <p>
            {geoError}{" "}
            {geoError.includes("Distance") && (
              <>
                Si le problème persiste, appelez-nous au{" "}
                <a href={COMPANY.phoneHref} className="font-semibold underline">
                  {COMPANY.phone}
                </a>
                .
              </>
            )}
          </p>
        </div>
      )}

      {/* Résultat */}
      {result && (
        <div
          data-testid={`calc-${variant}-result`}
          className={`mt-6 rounded-2xl border p-5 sm:p-6 ${
            dark
              ? "border-secondary/30 bg-secondary/5"
              : "border-primary/20 bg-primary/5"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Route className={`h-6 w-6 ${dark ? "text-secondary" : "text-primary"}`} />
              <div>
                <p className={`text-xs uppercase tracking-widest ${dark ? "text-slate-400" : "text-slate-500"}`}>
                  Distance estimée
                </p>
                <p
                  className={`font-heading text-2xl font-bold ${dark ? "text-[#FAFAF7]" : "text-foreground"}`}
                  data-testid={`calc-${variant}-distance`}
                >
                  {result.distanceKm} km
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className={`text-xs uppercase tracking-widest ${dark ? "text-slate-400" : "text-slate-500"}`}>
                Fourchette estimée
              </p>
              <p
                className={`font-heading text-2xl font-bold ${dark ? "text-secondary" : "text-primary"}`}
                data-testid={`calc-${variant}-price-range`}
              >
                Entre {formatEuro(result.low)} € et {formatEuro(result.high)} € HT
              </p>
            </div>
          </div>
          <p className={`mt-4 text-xs leading-relaxed ${dark ? "text-slate-400" : "text-slate-500"}`}>
            Cette estimation est indicative et ne constitue pas un devis. Le prix
            définitif dépend du type de marchandise, des conditions d'accès, des
            délais, des assurances et des options retenues. Un devis précis vous
            sera adressé sous 24 h ouvrées.
          </p>
          <button
            type="button"
            data-testid={`calc-${variant}-devis-button`}
            onClick={askQuote}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent px-6 py-3.5 font-heading text-base font-semibold text-white shadow-md transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[#EA580C] hover:shadow-xl sm:w-auto"
          >
            Demander un devis précis
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      )}

      {!result && !geoError && (
        <p
          className={`mt-6 text-center text-sm ${dark ? "text-slate-500" : "text-slate-400"}`}
          data-testid={`calc-${variant}-hint`}
        >
          Renseignez les deux adresses et le poids : l'estimation s'affiche
          automatiquement.
        </p>
      )}
    </div>
  );
};

export default QuoteCalculator;
